import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Generic tooltip component for displaying value details
 *
 * @param {React.ReactNode} children - The element that triggers the tooltip
 * @param {React.ReactNode} tooltipContent - JSX content to render inside the tooltip
 * @param {object} options - Additional configuration options
 * @param {string} options.className - Additional CSS classes for the container
 * @param {string} options.tooltipClassName - Additional CSS classes for the tooltip
 * @param {object} options.tooltipStyles - Additional styles for the tooltip
 */
function ValueTooltip({ children, options = {}, tooltipContent }) {
    const { className = '', tooltipClassName = '', tooltipStyles = {} } = options;

    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState('top');
    const [tooltipStyle, setTooltipStyle] = useState({});
    const containerRef = useRef(null);

    // Calculate position of the tooltip
    const calculatePosition = () => {
        if (containerRef.current) {
            const containerRect = containerRef.current.getBoundingClientRect();

            // Check if we're near the top of the viewport
            if (containerRect.top < 120) {
                setTooltipPosition('bottom');
            } else {
                setTooltipPosition('top');
            }

            // Calculate absolute position
            const style = {
                position: 'fixed',
                left: containerRect.left + containerRect.width / 2,
                transform: 'translateX(-50%)',
                minWidth: '160px',
                maxWidth: '180px',
                zIndex: 9999,
                ...tooltipStyles,
            };

            if (tooltipPosition === 'top') {
                style.bottom = window.innerHeight - containerRect.top + 8;
            } else {
                style.top = containerRect.bottom + 8;
            }

            setTooltipStyle(style);
        }
    };

    // Handle showing the tooltip
    const handleMouseEnter = () => {
        setShowTooltip(true);
        setTimeout(calculatePosition, 0);
    };

    // Handle hiding the tooltip
    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

    // Recalculate position when tooltip position changes
    useEffect(() => {
        if (showTooltip) {
            calculatePosition();
        }
    }, [tooltipPosition, showTooltip]);

    // Add window resize listener
    useEffect(() => {
        if (showTooltip) {
            window.addEventListener('resize', calculatePosition);
            window.addEventListener('scroll', calculatePosition, true);
        }

        return () => {
            window.removeEventListener('resize', calculatePosition);
            window.removeEventListener('scroll', calculatePosition, true);
        };
    }, [showTooltip]);

    // Render the tooltip content
    const renderTooltipContent = () => (
        <div
            className={`tooltip-popup inventory-history-v2 bg-white shadow-lg rounded-md p-1.5 text-xs border border-gray-200 ${tooltipClassName}`}
            style={tooltipStyle}
        >
            {tooltipContent}

            {/* Triangle pointing to the trigger element */}
            <div
                className="absolute w-2.5 h-2.5 bg-white border-r border-b border-gray-200 transform"
                style={{
                    ...(tooltipPosition === 'top'
                        ? {
                              bottom: '-6px',
                              left: '50%',
                              marginLeft: '-5px',
                              transform: 'rotate(45deg)',
                          }
                        : {
                              top: '-6px',
                              left: '50%',
                              marginLeft: '-5px',
                              transform: 'rotate(-135deg)',
                          }),
                }}
            />
        </div>
    );

    return (
        <div
            className={`relative inline-block tooltip-container ${className}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            ref={containerRef}
        >
            {children}

            {showTooltip && createPortal(renderTooltipContent(), document.body)}
        </div>
    );
}

export { ValueTooltip };
